import { FC, useState } from "react";

import { SectionEnrollmentTable } from "./SectionEnrollmentTable";

import { serverGetSectionEnrollmentSummary } from "api/api-server-data";
import { CourseMenu } from "components/menus/CourseMenu";
import { TermMenu } from "components/menus/TermMenu";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { ArrowDownIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import { selectTerms } from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { toastErrorMessage } from "utils/alerts";
import { downloadCSVFile } from "utils/file";
import { generateCourseEnrollmentCSV } from "utils/reporting";

/** Max rows to render in the table; full data still used for CSV download. */
const DISPLAY_ROW_CAP = 300;

export const SectionEnrollmentReport: FC = () => {
  const [selectedCourseId, setSelectedCourseId] = useState("");
  const [selectedTermId, setSelectedTermId] = useState("");
  const terms = useAppSelector(selectTerms);

  const { data, isLoading, error } = usePromiseEffect(async () => {
    if (!selectedTermId) {
      return [];
    }

    return serverGetSectionEnrollmentSummary({
      term_id: selectedTermId,
      course_id: selectedCourseId || undefined,
    });
  }, [selectedTermId, selectedCourseId]);

  const handleDownloadReport = () => {
    if (!selectedTermId) {
      toastErrorMessage(t("section_enrollment_report.select_term_to_view_data"));
      return;
    }

    if (!data || data.length === 0) {
      toastErrorMessage(t("section_enrollment_report.no_data"));
      return;
    }

    try {
      const selectedTerm = terms.find((term) => term.id === selectedTermId);
      const termTitle = selectedTerm?.title || selectedTermId;

      const { csvData, filename } = generateCourseEnrollmentCSV(data, termTitle);
      downloadCSVFile(csvData, filename);
    } catch {
      toastErrorMessage(t("section_enrollment_report.export_failed"));
    }
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="h3">{t("section_enrollment_report.title")}</h2>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <TermMenu
            defaultToAll={false}
            eventPrefix="Home - Reports / "
            menuType="menu"
            selectedTermId={selectedTermId}
            size="s"
            onTermChange={setSelectedTermId}
          />
        </div>

        <div className="flex items-center gap-2">
          <CourseMenu
            menuType="menu"
            selectedCourseId={selectedCourseId}
            size="s"
            onCourseChange={setSelectedCourseId}
          />
        </div>

        <div className="flex items-center gap-2">
          <Button
            disabled={!selectedTermId || !data || data.length === 0}
            eventName="Home - Reports / Download course enrollment"
            kind="white"
            size="s"
            onClick={handleDownloadReport}
          >
            <IconText
              iconStart={<ArrowDownIcon />}
              text={t("section_enrollment_report.download")}
            />
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && <div className="bg-red-50 text-red-700 mb-4 rounded-md p-4">{error.message}</div>}

      {/* Loading state */}
      {isLoading && (
        <div className="mt-8 text-center text-black-tint-40">{t("common.loading")}...</div>
      )}

      {/* Table: render only first N rows to keep UI responsive; full data used for CSV */}
      {data && (
        <>
          {data.length > DISPLAY_ROW_CAP && (
            <p className="mb-2 text-sm text-black-tint-40">
              {t("section_enrollment_report.showing_first_of", {
                count: DISPLAY_ROW_CAP,
                total: data.length,
              })}
            </p>
          )}

          <SectionEnrollmentTable data={data.slice(0, DISPLAY_ROW_CAP)} />
        </>
      )}

      {/* No data message */}
      {data && !error && data.length === 0 && (
        <div className="mt-8 text-center text-black-tint-40">
          {selectedTermId
            ? t("section_enrollment_report.no_data")
            : t("section_enrollment_report.select_term_to_view_data")}
        </div>
      )}
    </div>
  );
};
